import clsx from 'clsx';

//Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

import { put } from '@/utils/client';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

//Libs
import { USER_PROFILE_INFOS } from '@/lib/routes/apiEndpoints';

//Components
import Icon from '@/components/base/Icon';
import { SwitchWithIcons } from '@/components/base/Switch/SwitchWithIcons';

const getLeftIcon = () => {
    return (
        <Icon
            data-testid="switch-icon"
            icon="light"
            className="text-sm text-white transition-colors dark:text-icoDarkMode-wolf"
        />
    );
};

const getRightIcon = (enabled: boolean) => {
    return (
        <Icon
            data-testid="switch-icon"
            icon="dark"
            className={clsx('text-sm transition-colors', {
                'text-icoGray-400': !enabled,
                'text-icoDarkMode-green': enabled
            })}
        />
    );
};

const SwitchLightDarkMode = ({ setError }: { setError: (message: string) => void }) => {
    const { t } = useTranslation('home');
    const { user, mutateUser } = useUser();
    const { toggleDarkMode } = useDarkMode();

    const changeTheme = async (isDarkMode: boolean) => {
        // reset error
        setError(null);

        // toggle theme
        const theme = isDarkMode ? 'dark' : 'light';
        toggleDarkMode(theme);

        try {
            await mutateUser(
                {
                    ...user,
                    settings: {
                        ...user.settings,
                        theme
                    }
                },
                false
            );
            await put(USER_PROFILE_INFOS, { settings: { theme } });
        } catch (e) {
            //if request failed revert theme to previous one and display error
            setError(t('header.error_switch_theme'));

            const prevTheme = theme === 'light' ? 'dark' : 'light';
            toggleDarkMode(prevTheme);
            await mutateUser(
                {
                    ...user,
                    settings: {
                        ...user.settings,
                        theme: prevTheme
                    }
                },
                false
            );
        }
    };

    return (
        <SwitchWithIcons
            data-testid="switch-with-icons"
            size="big"
            enabled={user?.settings?.theme === 'dark'}
            setEnabled={changeTheme}
            className="bg-white dark:bg-icoDarkMode-deepBlack"
            dotClassName="bg-icoGreen dark:bg-icoDarkMode-green dark:bg-opacity-20"
            leftIcon={() => getLeftIcon()}
            rightIcon={({ enabled }) => getRightIcon(enabled)}
        />
    );
};

export default SwitchLightDarkMode;
